import { createClient } from "@connectrpc/connect";
import { grpcTransportProvider } from "../../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../../comun/services/grpc/mapConnectError";
import { createCachedFactory } from "../../../../utils/factoryUtils";
import {
  MonitoreoBrocaItem,
  MonitoreoBrocaService,
} from "../../../../generated/grpc/monitoreo_broca_pb";
import { grpcMonitoreoBrocaDatabase } from "../database/GrpcMonitoreoBrocaDatabase";
import { serviceSync } from "../../../../base/services/ServiceSync";
import { MonitoreoBrocaResponse } from "../response/MonitoreoBrocaResponse";

/** Variante gRPC-Web (con delta real, por finca) de
 *  `MonitoreoBrocaGetListByFincaIdService` (ver ese archivo). Devuelve
 *  `MonitoreoBrocaResponse[]` -mismo shape que ya usa el resto de la app-. */
class MonitoreoBrocaGrpcService {
  constructor(private readonly _fincaId: string) {}

  async getListAsync(): Promise<MonitoreoBrocaResponse[]> {
    await this._syncAsync();
    const items = await grpcMonitoreoBrocaDatabase.getListByFincaIdAsync<MonitoreoBrocaItem>(
      this._fincaId,
    );
    return items.map(toMonitoreoBrocaResponse);
  }

  clearCache(): void {
    serviceSync.reset(`grpc:monitoreo-broca:${this._fincaId}`);
  }

  private async _syncAsync(): Promise<void> {
    await serviceSync.run(`grpc:monitoreo-broca:${this._fincaId}`, async () => {
      const ultimaSincronizacion = await grpcMonitoreoBrocaDatabase.getUltimaSincronizacionAsync(
        this._fincaId,
      );
      const client = createClient(MonitoreoBrocaService, grpcTransportProvider.get());

      let response;
      try {
        response = await client.listByFincaId({
          fincaId: this._fincaId,
          ultimaSincronizacion,
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      await grpcMonitoreoBrocaDatabase.putDeltaAsync(
        this._fincaId,
        response.items,
        response.timestampSincronizacion,
      );
    });
  }
}

/** Factory cacheada: una única instancia por fincaId (comparte cooldown/caché,
 *  mismo criterio que `fx_createMonitoreoBrocaGetListByFincaIdService`). */
export const fx_createMonitoreoBrocaGrpcService = createCachedFactory(
  (fincaId: string) => new MonitoreoBrocaGrpcService(fincaId),
);

/** Adapta un `MonitoreoBrocaItem` (gRPC) al shape `MonitoreoBrocaResponse`
 *  (REST). Exportada porque el servicio de guardado también la necesita.
 *  `clienteId` y los derivados que el cliente siempre recalcula localmente
 *  (`arbolesLimpios`, `totales`, `porcentajePosicion1/2`, `porcentajeAusentes`,
 *  `porcentajeMuertas`, `promedioGranosXRama`, `infestacionGeneral`) se
 *  rellenan inertes: nadie los lee del lado response en toda la app (ver
 *  comentario del proto). */
export function toMonitoreoBrocaResponse(
  monitoreo: MonitoreoBrocaItem,
): MonitoreoBrocaResponse {
  return {
    id: monitoreo.id,
    isEliminado: monitoreo.eliminado,
    clienteId: "",
    fincaId: monitoreo.fincaId,
    loteId: monitoreo.loteId,
    loteNombre: monitoreo.loteNombre,
    fecha: monitoreo.fecha,
    anio: monitoreo.anio,
    semanaDelAnio: monitoreo.semanaDelAnio,
    arbolesEvaluados: monitoreo.arbolesEvaluados,
    arbolesBrocados: monitoreo.arbolesBrocados,
    arbolesLimpios: 0,
    frutosEvaluados: monitoreo.frutosEvaluados,
    frutosBrocados: monitoreo.frutosBrocados,
    posicion1: monitoreo.posicion1,
    posicion2: monitoreo.posicion2,
    ausentes: monitoreo.ausentes,
    muertas: monitoreo.muertas,
    totales: 0,
    porcentajePosicion1: 0,
    porcentajePosicion2: 0,
    porcentajeAusentes: 0,
    porcentajeMuertas: 0,
    promedioGranosXRama: 0,
    infestacionGeneral: 0,
    infestacionReal: monitoreo.infestacionReal,
    infestacionRealColor: monitoreo.infestacionRealColor as "low" | "mid" | "high",
    mortalidad: monitoreo.mortalidad,
    mortalidadColor: monitoreo.mortalidadColor as "low" | "mid" | "high",
  };
}
